import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '../utils/supabaseClient';

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const submitLockRef = useRef(false);
  const weakPasswords = new Set([
    'password',
    'password1',
    'password123',
    'qwerty',
    'qwerty123',
    '123456',
    '1234567',
    '12345678',
    '123456789',
    '111111',
    '000000',
    'abcdef',
    'abc123',
    'letmein',
    'welcome',
    'iloveyou',
    'admin',
    'admin123',
  ]);

  const passwordTrimmed = (password || '').trim();
  const passwordLower = passwordTrimmed.toLowerCase();
  const ruleMinLen = passwordTrimmed.length >= 8;
  const ruleUpper = /[A-Z]/.test(passwordTrimmed);
  const ruleDigit = /[0-9]/.test(passwordTrimmed);
  const ruleNotWeak = passwordTrimmed.length > 0 && !weakPasswords.has(passwordLower);

  useEffect(() => {
    // Supabase password recovery link returns tokens in URL hash.
    // We exchange/set the session so updateUser({password}) works.
    const init = async () => {
      try {
        // 1) Try implicit flow tokens from URL hash
        if (window.location.hash) {
          const hash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : window.location.hash;
          const params = new URLSearchParams(hash);
          const access_token = params.get('access_token');
          const refresh_token = params.get('refresh_token');

          if (access_token && refresh_token) {
            const { error: setErr } = await supabase.auth.setSession({ access_token, refresh_token });
            if (setErr) {
              setIsReady(true);
              return;
            }
          }
        }

        // 2) Try PKCE flow (?code=...)
        try {
          const { data: sessionData } = await supabase.auth.getSession();
          if (!sessionData.session) {
            const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(window.location.href);
            if (exchangeError) {
              setIsReady(true);
              return;
            }
          }
        } catch (e) {
          // ignore
        }

        setIsReady(true);
      } catch (e) {
        setIsReady(true);
      }
    };

    init();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting || submitLockRef.current) return;
    submitLockRef.current = true;

    if (!password || !confirmPassword) {
      toast.error('Lütfen tüm alanları doldurun');
      submitLockRef.current = false;
      return;
    }

    if (password.length < 8) {
      toast.error('Şifre en az 8 karakter olmalıdır');
      submitLockRef.current = false;
      return;
    }

    if (!/[A-Z]/.test(password)) {
      toast.error('Şifre en az 1 büyük harf içermelidir');
      submitLockRef.current = false;
      return;
    }

    if (!/[0-9]/.test(password)) {
      toast.error('Şifre en az 1 rakam içermelidir');
      submitLockRef.current = false;
      return;
    }

    if (weakPasswords.has((password || '').trim().toLowerCase())) {
      toast.error('Bu şifre çok zayıf. Lütfen daha güçlü bir şifre seçin.');
      submitLockRef.current = false;
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      submitLockRef.current = false;
      return;
    }

    try {
      setIsSubmitting(true);
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        toast.error('Şifre sıfırlama oturumu bulunamadı. Lütfen e-postadaki linki tekrar açın.');
        submitLockRef.current = false;
        return;
      }

      const { error } = await supabase.auth.updateUser({ password });
      if (error) {
        toast.error(error.message);
        submitLockRef.current = false;
        return;
      }

      toast.success('Şifreniz güncellendi. Giriş yapabilirsiniz.');
      await supabase.auth.signOut();
      navigate('/giris');
    } finally {
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block">
              <h1 className="font-manrope font-bold text-3xl text-trendyol-orange">Alışveriş Burada</h1>
            </Link>
            <p className="text-gray-600 mt-2">Yeni Şifre Belirle</p>
          </div>

          {!isReady ? (
            <div className="text-sm text-gray-600">Doğrulanıyor...</div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors"
                  placeholder="En az 8 karakter"
                />
                <div className="mt-2 text-xs">
                  <div className={ruleMinLen ? 'text-green-600' : 'text-gray-600'}>
                    En az 8 karakter
                  </div>
                  <div className={ruleUpper ? 'text-green-600' : 'text-gray-600'}>
                    En az 1 büyük harf
                  </div>
                  <div className={ruleDigit ? 'text-green-600' : 'text-gray-600'}>
                    En az 1 rakam
                  </div>
                  <div className={ruleNotWeak ? 'text-green-600' : 'text-gray-600'}>
                    Zayıf şifre listesinde olmamalı
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Yeni Şifre Tekrar</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-trendyol-orange focus:outline-none transition-colors"
                  placeholder="Şifrenizi tekrar girin"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-trendyol-orange hover:bg-orange-600 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Şifreyi Güncelle
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <Link to="/giris" className="text-sm text-trendyol-orange hover:underline">
              Giriş sayfasına dön
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
